import { useState, useMemo, useEffect } from "react";
import { useBusinessNoticeSenderList } from "~/services/business/notify";
import type { BusinessNotifySnapshot } from "&/businessNotifyList";

const useNotifySend = (initial?: BusinessNotifySnapshot) => {
  const [filterType, setFilterType] = useState<string>(
    initial?.filterType ?? "전체"
  );
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [selectedType, setSelectedType] = useState(
    initial?.selectedType ?? "최신순"
  );
  const [searchText, setSearchText] = useState(initial?.searchText ?? "");
  const [searchKeyword, setSearchKeyword] = useState<string | null>(
    initial?.searchKeyword ?? null
  ); // 실제 검색에 사용될 키워드
  const [page, setPage] = useState(initial?.page ?? 1);
  // 복원 부트스트랩: 저장된 페이지 수만큼(page*10)을 1페이지 한 번에 재조회 후 교체.
  // 다음 페이지를 붙이거나 검색/정렬이 바뀌는 순간 일반 페이징(size 10)으로 전환
  const [bootstrap, setBootstrap] = useState(!!initial);
  const [accumulatedContent, setAccumulatedContent] = useState<any[]>([]);

  // API 요청 파라미터
  const requestParams = useMemo(
    () => ({
      searchKeyword: searchKeyword, // 검색 버튼 클릭 시에만 업데이트되는 키워드 사용
      cdRecipientType: null,
      noticeType: null,
      page: bootstrap ? 1 : page,
      size: bootstrap ? page * 10 : 10,
      readYn: null,
      sortOrder: selectedType === "최신순" ? "DESC" : "ASC",
      replyYn: null,
    }),
    [page, selectedType, filterType, searchKeyword, bootstrap]
  );

  // API 호출
  const {
    data: apiData,
    isLoading,
    error,
    refetch,
  } = useBusinessNoticeSenderList(requestParams);

  // 페이지별 데이터 누적 (스크롤 시 추가 로드)
  useEffect(() => {
    if (!apiData?.content) return;
    const currentPage = requestParams.page;
    setAccumulatedContent((prev) =>
      currentPage === 1 ? apiData.content : [...prev, ...apiData.content]
    );
  }, [apiData, requestParams.page]);

  const sentData = useMemo(() => {
    if (apiData && Array.isArray(apiData)) {
      return apiData;
    }
    return accumulatedContent;
  }, [apiData, accumulatedContent]);

  const handleSearch = (value: string) => {
    setBootstrap(false);
    setSearchKeyword(value || null); // 검색 버튼 클릭 시에만 실제 검색 키워드 업데이트
    setPage(1); // 검색 시 첫 페이지로 리셋
    // searchKeyword가 변경되면 requestParams가 변경되고,
    // useBusinessNoticeSenderList가 자동으로 재호출됩니다.
  };

  const onLoad = () => {
    // 부트스트랩(일괄 조회) 이후 다음 페이지부터는 일반 페이징으로 전환
    setBootstrap(false);
    setPage((prev) => prev + 1); // 다음 페이지 로드
  };

  const hasMore = apiData ? !apiData.last : false;

  return {
    sentData,
    apiData,
    hasMore,
    onLoad,
    handleSearch,
    filterType,
    setFilterType: (type: string) => {
      setBootstrap(false);
      setFilterType(type);
      setPage(1); // 필터 변경 시 첫 페이지로 리셋
      setSearchKeyword(null); // 필터 변경 시 검색 키워드 초기화
    },
    dropdownOpen,
    setDropdownOpen,
    selectedType,
    setSelectedType: (type: string) => {
      setBootstrap(false);
      setSelectedType(type);
      setPage(1); // 정렬 변경 시 첫 페이지로 리셋
      setSearchKeyword(null); // 정렬 변경 시 검색 키워드 초기화
    },
    isLoading,
    error,
    refetch,
    page,
    searchKeyword,
    searchText,
    setSearchText,
  };
};

export default useNotifySend;
